import { useAccount } from "@/hooks/useAccount";
import { useAuth } from "@/hooks/useAuth";
import { updateAppUser } from "@/services/appuser.queries";
import { useToastNotify } from "@/stores/toastStore";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { getStorageArrayLength, getValueFromStorage, popFromStorageArray } from "@/utils/secureStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { useCallback, useState } from "react";

export const useSelectPlate = () => {
  const [inputValue, setInputValue] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [selectedPlate, setSelectedPlate] = useState("");
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [plateToRemove, setPlateToRemove] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);
  const [guestPlates, setGuestPlates] = useState<string[] | null>(null);
  const [errors, setErrors] = useState<{
    radio: string | null;
    licensePlate: string | null;
    contact?: string | null;
  }>({
    radio: null,
    licensePlate: null,
  });
  const { notify } = useToastNotify();
  const { account, isFetching: isAccountFetching, isPending: isAccountPending, refetch: refetchAccount } = useAccount();

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchGuestPlates = useCallback(async () => {
    const guestPlates = await getValueFromStorage("guestRecentPlates");
    if (guestPlates) {
      const array: string[] = JSON.parse(guestPlates);
      setGuestPlates(array);
    }
  }, []);

  const plateMutation = useMutation({
    mutationFn: updateAppUser,
    onError: (error) => {
      notify({
        severity: "error",
        message: t("An error occurred when updating user data"),
      });
      console.error("Error updating user data: ", error);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["account"] });
    },
  });

  const handleDeletePlate = (plateToDelete: string) => {
    setPlateToRemove(plateToDelete);
    setIsRemoveModalOpen(true);
  };

  const resetStates = () => {
    setInputValue("");
    setLicensePlate("");
    setSelectedPlate("");
  };

  const confirmRemovePlate = async () => {
    setIsRemoving(true);

    if (!account || !user) {
      await popFromStorageArray("guestRecentPlates", plateToRemove);
      const length = await getStorageArrayLength("guestRecentPlates");
      if (length === 0) {
        resetStates();
      }
      fetchGuestPlates();
    } else {
      const updatedPlates = account.savedPlates.filter((plate) => plate.plate !== plateToRemove);
      const updatedAccountData = {
        ...account,
        savedPlates: updatedPlates,
        cognitoIDToken: user.idToken ?? "",
      };
      await plateMutation.mutateAsync(updatedAccountData);
      if (updatedPlates.length === 0) {
        resetStates();
      }
    }

    setIsRemoving(false);
    setIsRemoveModalOpen(false);
  };

  const handleLicensePlateChange = (plate: string) => {
    setLicensePlate(plate);
  };

  const clearLicensePlateError = () => {
    setErrors((prevErrors) => ({ ...prevErrors, licensePlate: null }));
  };

  const validatePlateInput = () => {
    return validateLicensePlate(inputValue);
  };

  return {
    fetchGuestPlates,
    isAccountPending,
    guestPlates,
    inputValue,
    setInputValue,
    licensePlate,
    setLicensePlate,
    selectedPlate,
    setSelectedPlate,
    isRemoveModalOpen,
    setIsRemoveModalOpen,
    plateToRemove,
    isRemoving,
    errors,
    setErrors,
    handleDeletePlate,
    confirmRemovePlate,
    handleLicensePlateChange,
    clearLicensePlateError,
    validatePlateInput,
    account,
    isAccountFetching,
    refetchAccount,
  };
};
